import React, { useState, useEffect, useRef } from 'react';
import { useIsMobileLayout } from '@/hooks';
import Draggable from 'react-draggable';
import { useDispatch, useSelector } from 'react-redux';
import { updatePositionParticipant } from '@/redux/slices/document';
import './index.css';
import { SIGNATURE_GEOMETRY } from '@/constants';

const MOBILE_BREAKPOINT = 991;
const useIsMobile = () => {
  const isMobile = useIsMobileLayout();
  return isMobile;
};

export default function Sign ({ page }) {
  const dispatch = useDispatch();
  const [blinkVisible, setBlinkVisible] = useState(true);
  const { positions, signsSelected, ignoreSignatureLines, documentInfo, dimensions, hasExistingSignatureLines } =
    useSelector(({ document, signs }) => ({
      positions: document.positions,
      signsSelected: signs.signsSelected,
      ignoreSignatureLines: document.ignoreSignatureLines,
      documentInfo: document.documentInfo,
      dimensions: document.dimenssions,
      hasExistingSignatureLines: document.hasExistingSignatureLines
    }));
  const userInfo = useSelector(({ userInfo }) => userInfo);
  const isMobile = useIsMobile();
  const nodeRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => setBlinkVisible(v => !v), 500);
    return () => clearInterval(interval);
  }, []);

  // escalas PDF → px pantalla
  const scaleX = dimensions.width / documentInfo.width;
  const scaleY = dimensions.height / documentInfo.height;

  // tamaños del blink‐box vs. línea
  // Nota: En mobile se usan valores más pequeños para la firma visual.
  // Pero si el dueño fijó la posición en Desktop, las coordenadas (x,y) ya son relativas al PDF original.
  // El problema reportado es que en móvil se ve "desfasado" y "más pequeño".
  // Si escalamos blinkWidth/Height, el cuadro se hace minúsculo si scaleX es pequeño (en mobile scaleX suele ser < 0.5).
  // La lógica anterior usaba valores fijos (isMobile ? 100 : 200).
  // Para arreglar el zoom SIN romper el mobile, debemos escalar, PERO quizás ajustar la base para mobile.

  // Restauramos tamaños base similares a la lógica original pero escalados para que respondan al zoom.
  // Si isMobile es true, el scaleX ya será pequeño (ej. 0.4).
  // Si multiplicamos 100 * 0.4 = 40px (muy chico).
  // Originalmente era: isMobile ? 100 : 200 (fijo, sin multiplicar por scaleX).
  // Eso significaba que en mobile (pantalla chica) la firma era de 100px FÍSICOS.
  // Si queremos mantener ese tamaño visual relativo al documento, debemos dividir por el scale "promedio" de mobile o usar una base mayor.
  // O mejor: Definir el tamaño en "unidades PDF" y dejar que scaleX lo reduzca naturalmente.
  // 200 unidades PDF * scaleX (0.4) = 80px.
  // 200 unidades PDF * scaleX (1.0) = 200px.
  // Esto hace que la firma sea proporcional al documento en ambos dispositivos.

  // CAMBIO: Usar un tamaño base unificado (o ajustado levemente) y multiplicarlo por la escala.
  // Esto asegura que la firma ocupe el mismo % del documento en Desktop y Mobile.
  const baseWidthPdf = 200; // Tamaño base en unidades PDF
  const baseHeightPdf = 65;
  const baseLineWidthPdf = 180;
  const baseLineHeightPdf = 16;

  // En móvil, a veces se quiere que la firma sea un poco más grande visualmente para que sea tocable.
  // Si dejamos 200 * 0.3 = 60px puede ser difícil de manipular.
  // Pero el usuario dice "el cuadro rojo es más pequeño de como era antes".
  // Antes: Mobile = 100px fijos.
  // Ahora: 100 * scaleX. Si scaleX < 1, es más chico.
  // Si queremos que en mobile se vea como antes (100px aprox), y el scaleX es ~0.4,
  // entonces base * 0.4 = 100 => base = 250.
  // Vamos a intentar unificar para que la posición sea consistente.

  // Documento con líneas existentes (selector): el recuadro es la huella EXACTA de la firma estampada
  // (constants/signatureGeometry.js), igual en desktop y mobile. Sin líneas: tamaños históricos.
  const existingLinesGeometry = SIGNATURE_GEOMETRY.SIMPLE_EXISTING_LINES;
  const blinkWidth = hasExistingSignatureLines
    ? existingLinesGeometry.width * scaleX
    : (isMobile ? 180 : 200) * scaleX;
  const blinkHeight = hasExistingSignatureLines
    ? existingLinesGeometry.height * scaleY
    : (isMobile ? 60 : 65) * scaleY;
  const lineWidthPx = (isMobile ? 200 : 180) * scaleX;
  // Altura TOTAL del elemento SignPos en unidades PDF (lineHeightBase + lineExtraBase):
  //   Desktop: 16 + 15 = 31 | Mobile: 10 + 10 = 20
  const elementHeightPdf = hasExistingSignatureLines
    ? (isMobile ? 20 : 16)   // líneas existentes: solo lineHeightBase
    : (isMobile ? 20 : 31);  // líneas colocadas: lineHeightBase + lineExtraBase
  const lineHeightPx = elementHeightPdf * scaleY;

  // ── OFFSETS X ───────────────────────
  const baseOffsetX = (lineWidthPx - blinkWidth) / 2;
  const relativeShiftX = lineWidthPx * 0.10;
  const extraOffsetX = isMobile ? relativeShiftX * 0.3 : relativeShiftX;
  const offsetX = hasExistingSignatureLines
    ? existingLinesGeometry.offsetX * scaleX
    : baseOffsetX + extraOffsetX;

  // ── OFFSETS Y ───────────────────────
  // Queremos que el FONDO del cuadro rojo coincida con la línea visible (borderBottom del elemento).
  // La línea está en: currentPosition.y + elementHeightPdf  (en PDF units)
  // FONDO del blink box: currentPosition.y * scaleY + offsetY + blinkHeight
  // Igualando: offsetY = (elementHeightPdf - blinkHeightPdf) * scaleY = lineHeightPx - blinkHeight
  const offsetY = hasExistingSignatureLines
    ? existingLinesGeometry.offsetY * scaleY                // mismo offset con el que se estampa (savePdfSigns)
    : lineHeightPx - blinkHeight;                           // bottom del blink = línea visible

  // posición de la firma y clon de la línea
  const currentPosition = positions[page]?.find(
    pos => pos.email === userInfo.email && pos.type === 'signature'
  ) || null;
  const linePosition = positions[page]?.find(
    pos => pos.email === userInfo.email && pos.type === 'lineOfSignature'
  ) || null;

  // si NO hay 'signature' pero sí 'lineOfSignature', la clonamos
  useEffect(() => {
    if (!ignoreSignatureLines && !currentPosition && linePosition) {
      dispatch(updatePositionParticipant({
        participant: userInfo,
        page,
        x: linePosition.x,
        y: linePosition.y,
        type: 'signature',
        signed: true,
        rotation: linePosition.rotation ?? 0
      }));
    }
  }, [ignoreSignatureLines, currentPosition, linePosition, dispatch, page, userInfo]);

  // ROTACIÓN
  const [rotation, setRotation] = useState(0);
  useEffect(() => {
    if (currentPosition?.rotation !== undefined) {
      setRotation(currentPosition.rotation);
    }
  }, [currentPosition]);

  const [isHovering, setIsHovering] = useState(false);
  const [showRotationControl, setShowRotationControl] = useState(false);
  const [isDraggingDisabled, setIsDraggingDisabled] = useState(false);

  // El ícono 🛠️ y el control de rotación se renderizan fuera (arriba) de la caja
  // que dispara el mouseenter/mouseleave, así que hay un pequeño "hueco" en pantalla
  // entre ambos. Sin este pequeño margen, el cursor cruza ese hueco al desplazarse
  // hacia el control y dispara mouseleave antes de llegar, ocultándolo. Damos un
  // pequeño margen de tiempo (y lo cancelamos si el mouse vuelve a entrar, o si el
  // usuario está arrastrando el slider) para que ese cruce no cierre el control.
  const hideTimeoutRef = useRef(null);
  const isRotationDraggingRef = useRef(false);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  const cancelHide = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    cancelHide();
    setIsHovering(true);
  };
  const handleMouseLeave = () => {
    cancelHide();
    hideTimeoutRef.current = setTimeout(() => {
      if (isRotationDraggingRef.current) return;
      setIsHovering(false);
      setShowRotationControl(false);
    }, 300);
  };
  const handleIconClick = () => { setShowRotationControl(true); setIsDraggingDisabled(true); };
  const handleRotationChange = e => setRotation(Math.round(e.target.value));
  const handleRotationStart = () => { isRotationDraggingRef.current = true; };
  const handleRotationEnd = () => {
    isRotationDraggingRef.current = false;
    dispatch(updatePositionParticipant({
      participant: userInfo,
      page,
      x: currentPosition.x,
      y: currentPosition.y,
      type: 'signature',
      signed: true,
      rotation
    }));
    setIsDraggingDisabled(false);
  };

  // Red de seguridad: si el usuario suelta el mouse fuera del propio slider mientras
  // lo arrastra, el evento de mouseup nativo del input casi siempre lo captura igual,
  // pero por si acaso liberamos aquí también la bandera de "arrastrando" para no
  // dejar el control bloqueado abierto para siempre.
  useEffect(() => {
    if (!showRotationControl) return;
    const onWindowMouseUp = () => { isRotationDraggingRef.current = false; };
    window.addEventListener('mouseup', onWindowMouseUp);
    return () => window.removeEventListener('mouseup', onWindowMouseUp);
  }, [showRotationControl]);

  // ── Touch drag manual para mobile ──────────────────────────────
  const touchStartRef = useRef(null);
  const positionRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!isMobile || !nodeRef.current) return;
    const el = nodeRef.current;

    const onTouchStart = (e) => {
      if (isDraggingDisabled) return;
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      // El visor móvil puede estar con zoom (pinch / TransformWrapper): el dedo se mueve en
      // píxeles de pantalla pero la firma vive en coordenadas del contenido. Se mide la escala
      // real del contenedor (no rotado) para que la firma siga al dedo 1:1 con cualquier zoom.
      const host = el.parentElement;
      const zoom = host && host.offsetWidth ? host.getBoundingClientRect().width / host.offsetWidth : 1;
      touchStartRef.current = { clientX: touch.clientX, clientY: touch.clientY, zoom: zoom > 0 ? zoom : 1 };
      positionRef.current = {
        x: currentPosition.x * scaleX + offsetX,
        y: currentPosition.y * scaleY + offsetY
      };
    };

    const onTouchMove = (e) => {
      if (!touchStartRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      const { zoom } = touchStartRef.current;
      const dx = (touch.clientX - touchStartRef.current.clientX) / zoom;
      const dy = (touch.clientY - touchStartRef.current.clientY) / zoom;
      let newPx = positionRef.current.x + dx;
      let newPy = positionRef.current.y + dy;
      // Igual que bounds="parent" del arrastre de escritorio: la firma no sale de la hoja.
      const host = el.parentElement;
      if (host) {
        const maxX = Math.max(0, host.offsetWidth - el.offsetWidth);
        const maxY = Math.max(0, host.offsetHeight - el.offsetHeight);
        newPx = Math.min(Math.max(newPx, 0), maxX);
        newPy = Math.min(Math.max(newPy, 0), maxY);
      }
      el.style.transform = `translate(${newPx}px, ${newPy}px)`;
      el._pendingPos = { x: newPx, y: newPy };
    };

    const onTouchEnd = (e) => {
      if (!touchStartRef.current) return;
      e.preventDefault();
      touchStartRef.current = null;
      if (!el._pendingPos) return;
      const { x: px, y: py } = el._pendingPos;
      el._pendingPos = null;
      el.style.transform = '';

      // px/py ya son coordenadas en píxeles de pantalla (incluyendo offsetX/Y)
      const origX = (px - offsetX) / scaleX;
      const origY = (py - offsetY) / scaleY;
      const deltaX = origX - currentPosition.x;
      const deltaY = origY - currentPosition.y;

      dispatch(updatePositionParticipant({
        participant: userInfo,
        page,
        x: origX,
        y: origY,
        type: 'signature',
        signed: true,
        rotation
      }));

      if (positions) {
        Object.keys(positions).forEach((key) => {
          const pageNum = parseInt(key);
          if (pageNum === page) return;
          const signaturePos = positions[key]?.find(
            (pos) => pos.email === userInfo.email && pos.type === 'signature'
          );
          if (signaturePos) {
            dispatch(updatePositionParticipant({
              participant: userInfo,
              page: pageNum,
              x: signaturePos.x + deltaX,
              y: signaturePos.y + deltaY,
              type: 'signature',
              signed: true,
              rotation
            }));
          }
        });
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobile, isDraggingDisabled, currentPosition, scaleX, scaleY, offsetX, offsetY, rotation, page, positions, userInfo, dispatch]);

  // dueño vs. invitado
  const isOwner = typeof userInfo.status === 'undefined';
  const shouldRender = currentPosition && (
    isOwner
      ? !(documentInfo.status === 'send' && documentInfo.owner_sign === 1)
      : userInfo.status !== 'signed'
  );
  if (!shouldRender) return null;

  return (
    <Draggable
      nodeRef={nodeRef}
      bounds="parent"
      position={{
        x: currentPosition.x * scaleX + offsetX,
        y: currentPosition.y * scaleY + offsetY
      }}
      disabled={isDraggingDisabled || isMobile}
      onStop={(e, data) => {
        const origX = (data.x - offsetX) / scaleX;
        const origY = (data.y - offsetY) / scaleY;

        // Calculamos el desplazamiento (delta) respecto a la posición original
        const deltaX = origX - currentPosition.x;
        const deltaY = origY - currentPosition.y;

        // Actualizamos la posición en la página actual
        dispatch(updatePositionParticipant({
          participant: userInfo,
          page,
          x: origX,
          y: origY,
          type: 'signature',
          signed: true,
          rotation
        }));

        // Sincronizamos la posición en las demás páginas usando el delta
        if (positions) {
          Object.keys(positions).forEach((key) => {
            const pageNum = parseInt(key);
            // Saltamos la página actual ya que ya se actualizó arriba
            if (pageNum === page) return;

            const pagePositions = positions[key];
            const signaturePos = pagePositions.find(
              (pos) => pos.email === userInfo.email && pos.type === 'signature'
            );

            if (signaturePos) {
              dispatch(updatePositionParticipant({
                participant: userInfo,
                page: pageNum,
                x: signaturePos.x + deltaX,
                y: signaturePos.y + deltaY,
                type: 'signature',
                signed: true,
                rotation
              }));
            }
          });
        }
      }}
    >
      <div
        ref={nodeRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ position: 'absolute', zIndex: isMobile ? 10 : 9, touchAction: isMobile ? 'none' : 'auto' }}
      >
        <div
          style={{
            position: 'relative',
            width: blinkWidth,
            height: blinkHeight,
            border: '2px solid #285FF6',
            opacity: blinkVisible ? 1 : 0.15,
            transform: `rotate(${rotation}deg)`,
            transition: 'transform 0.2s ease-in-out',
            // Capa de composición propia: en iOS (Safari y Chrome, ambos WebKit) un elemento con
            // opacidad que se agrega encima del <canvas> de una hoja que ya está en pantalla puede no
            // pintarse hasta que se fuerza un repintado (la hoja visible no lo mostraba y las demás sí).
            willChange: 'opacity, transform',
            WebkitBackfaceVisibility: 'hidden'
          }}
        >
          <img
            draggable={false}
            src={signsSelected?.img}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
        {/* El ícono de herramientas y el control de rotación quedan fuera de la caja
            que parpadea, para que no hereden su parpadeo y se puedan usar con normalidad. */}
        {isHovering && !showRotationControl && (
          <div
            onClick={handleIconClick}
            style={{ position: 'absolute', top: 10, right: 10, cursor: 'pointer' }}
          >
            🛠️
          </div>
        )}
        {showRotationControl && (
          <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ position: 'absolute', top: -40 }}
          >
            <input
              type="range"
              min={-45}
              max={45}
              step={1}
              value={rotation}
              onChange={handleRotationChange}
              onMouseDown={handleRotationStart}
              onMouseUp={handleRotationEnd}
            />
            <label style={{ marginLeft: 8 }}>Rotación: {rotation}º</label>
          </div>
        )}
      </div>
    </Draggable>
  );
}
